import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { keys } from "@/lib/queries";
import type { ProfileResponse, PreferencesResponse, MFAFactor, SessionItem, AuthEventItem, PageInfo } from "@/lib/types";
import { Tabs } from "@/components/ui";
import { ProfileSection } from "@/components/settings/ProfileSection";
import { SecuritySection } from "@/components/settings/SecuritySection";
import { PreferencesSection } from "@/components/settings/PreferencesSection";
import { ActivitySection } from "@/components/settings/ActivitySection";
import { buildHead } from "@/lib/seo";

export const Route = createFileRoute("/settings-preview")({
  head: () => buildHead({ path: "/settings-preview", title: "Settings preview (dev)", noindex: true }),
  component: Preview,
});

const page: PageInfo = { total: 1, limit: 20, offset: 0, has_more: false };
const profile = {
  user_id: "u1",
  email: "satoshi@example.com",
  display_name: "Satoshi",
  status: "ACTIVE",
  roles: ["trader"],
  permissions: [],
  created_at: new Date().toISOString(),
} as unknown as ProfileResponse;
const prefs = { preferences: { "notifications.order_fills": true, "trading.auto_redeem.enabled": false } } as unknown as PreferencesResponse;
const factors = [
  { factor_id: "f1", factor_kind: "TOTP", status: "ACTIVE", friendly_name: "My phone", registered_at: new Date().toISOString() },
] as unknown as MFAFactor[];
const sessions = [
  { session_id: "s1", device_label: "Chrome · macOS", ip_address: "192.0.2.1", city: "Bangkok", country: "TH", last_seen_at: new Date().toISOString(), is_current: true },
  { session_id: "s2", device_label: "Safari · iPhone", ip_address: "192.0.2.9", city: "Bangkok", country: "TH", last_seen_at: new Date(Date.now() - 86_400_000).toISOString() },
] as unknown as SessionItem[];
const auditSeed = [
  { event_type: "LOGIN_SUCCEEDED", event_severity: "INFO", ip_address: "192.0.2.1", city: "Bangkok", country: "TH", user_agent: "Mozilla/5.0 (Macintosh) Chrome/120", provider_kind: "EMAIL" },
  { event_type: "MFA_CHALLENGE_FAILED", event_severity: "WARN", ip_address: "192.0.2.9", city: "Bangkok", country: "TH", user_agent: "Mozilla/5.0 (iPhone) Safari/17", factor_kind: "TOTP", reason_message: "Invalid code" },
  { event_type: "LOGIN_FAILED", event_severity: "ALERT", ip_address: "203.0.113.7", city: "Unknown", country: "RU", user_agent: "curl/8.1", reason_message: "Password mismatch (5th attempt)" },
  { event_type: "SESSION_REVOKED", event_severity: "INFO", ip_address: "192.0.2.1", city: "Bangkok", country: "TH", user_agent: "Mozilla/5.0 (Macintosh) Chrome/120" },
];
// 14 rows so the mock spans two pages at the default 10 / page.
const audit = Array.from({ length: 14 }, (_, i) => ({
  ...auditSeed[i % auditSeed.length],
  event_id: `e${i + 1}`,
  occurred_at: new Date(Date.now() - i * 3_600_000).toISOString(),
})) as unknown as AuthEventItem[];
const auditPageInfo = (offset: number, count: number): PageInfo => ({ total: audit.length, limit: 10, offset, has_more: offset + count < audit.length });

function Preview() {
  const qc = useQueryClient();
  useState(() => {
    qc.setQueryData(keys.profile(), profile);
    qc.setQueryData(keys.preferences(), prefs);
    qc.setQueryData(keys.mfaFactors(), { items: factors, page });
    qc.setQueryData(keys.sessions(), { items: sessions, page });
    qc.setQueryData(keys.auditPage(10, 0), { items: audit.slice(0, 10), page: auditPageInfo(0, 10) });
    qc.setQueryData(keys.auditPage(10, 10), { items: audit.slice(10), page: auditPageInfo(10, audit.length - 10) });
    return true;
  });
  const [tab, setTab] = useState("profile");
  return (
    <div className="mx-auto min-h-dvh max-w-2xl bg-bg p-6 text-fg">
      <h1 className="mb-4 font-display text-2xl font-bold">Settings preview (mock)</h1>
      <Tabs
        className="mb-5"
        value={tab}
        onValueChange={setTab}
        items={[
          { value: "profile", label: "Profile" },
          { value: "security", label: "Security" },
          { value: "preferences", label: "Preferences" },
          { value: "activity", label: "Activity" },
        ]}
      />
      {tab === "security" ? <SecuritySection /> : tab === "preferences" ? <PreferencesSection /> : tab === "activity" ? <ActivitySection /> : <ProfileSection />}
    </div>
  );
}
